import {Component, Input} from '@angular/core';
import {AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';

/** Backend limit (DeactivateContractHandler::REASON_MAX_LENGTH, UNIVAPLAT-3498); the API trims first. */
export const CONTRACT_REASON_MAX_LENGTH = 255;

function reasonMaxLength(control: AbstractControl<string | null>): ValidationErrors | null {
    const length = (control.value ?? '').trim().length;
    return length > CONTRACT_REASON_MAX_LENGTH ? {maxlength: {max: CONTRACT_REASON_MAX_LENGTH, actual: length}} : null;
}

/**
 * UNIVAPLAT-3519: deactivate-contract modal with an optional reason.
 *
 * Opened by the History tab via NgbModal (`ariaLabelledBy: 'deactivate-contract-title'`), which
 * supplies role=dialog, focus trap, Escape and focus return. Closes with `true` when the contract
 * state changed (or the API says it already changed) so the caller reloads; Cancel, the close icon
 * and Escape dismiss without a call. The reason is personal data: never logged, dropped on destroy.
 * The backend owns the error text, shown verbatim.
 */
@Component({
    selector: 'cdss-deactivate-contract-modal',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './deactivate-contract.modal.html'
})
export class DeactivateContractModal {
    @Input() contractId!: number;

    readonly maxLength = CONTRACT_REASON_MAX_LENGTH;
    readonly reason = new FormControl<string>('', {nonNullable: true, validators: [reasonMaxLength]});
    saving = false;

    constructor(
        public modal: NgbActiveModal,
        private contractsService: ContractsService,
        private toastr: ToastrService,
    ) {
    }

    get length(): number {
        return this.reason.value.trim().length;
    }

    async submit(): Promise<void> {
        if (this.saving || this.reason.invalid) {
            return;
        }
        this.saving = true;
        try {
            const reason = this.reason.value.trim();
            await this.contractsService.deactivate(this.contractId, reason !== '' ? reason : undefined);
            this.toastr.success('Contract deactivated', 'Success');
            this.modal.close(true);
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
            if (error?.status === 409) {
                // Already deactivated (or read-only) meanwhile: close so the caller refreshes the state.
                this.modal.close(true);
            }
        } finally {
            this.saving = false;
        }
    }
}
